import { useState } from 'react';
import {
  Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip, Stack, Button,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, Snackbar,
} from '@mui/material';
import { Download, CheckCircle, Payments, AccountBalanceWallet, ReceiptLong, Warning, Savings } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { MiniBarChart } from '../../components/MiniBarChart';
import { EmptyState } from '../../components/EmptyState';
import { dealerPaymentsApi, type DealerPayment, type MarkPaidInput } from '../../api/dealerPayments.api';
import { FileUploadField } from '../../components/FileUploadField';
import { dealersApi } from '../../api/dealers.api';
import { extractApiError } from '../../api/error';

const inr = (x = 0) => `₹${Number(x).toLocaleString('en-IN')}`;
const PAY_MODES = ['neft', 'rtgs', 'upi', 'cheque', 'cash', 'adjustment'];
const statusColor = (s: string): 'default' | 'success' | 'warning' | 'error' =>
  s === 'reconciled' ? 'success' : s === 'disputed' ? 'error' : 'warning';
const statusLabel = (s: string) => (s === 'reconciled' ? 'Paid' : s === 'disputed' ? 'Disputed' : 'Unpaid');

/** Ageing (days) of an unpaid invoice vs its due date. */
function ageing(inv: DealerPayment): { text: string; overdue: boolean } {
  if (inv.reconciliationStatus === 'reconciled') return { text: '—', overdue: false };
  if (!inv.dueDate) return { text: 'no due date', overdue: false };
  const days = Math.floor((Date.now() - new Date(`${inv.dueDate}T00:00:00`).getTime()) / 86400000);
  if (days > 0) return { text: `${days}d overdue`, overdue: true };
  return { text: days === 0 ? 'due today' : `${-days}d to due`, overdue: false };
}

function ChartCard({ title, data }: { title: string; data: Array<{ label: string; value: number }> }) {
  return (
    <Card sx={{ height: '100%' }}><CardHeader title={title} titleTypographyProps={{ variant: 'subtitle1' }} />
      <CardContent>{data?.some((d) => d.value > 0) ? <MiniBarChart data={data.map((d) => ({ ...d, color: '#1e40af' }))} valueFormatter={(v) => inr(v)} /> : <EmptyState title="No data yet" dense />}</CardContent>
    </Card>
  );
}

export function DealerFinancePage() {
  const qc = useQueryClient();
  const [payFor, setPayFor] = useState<DealerPayment | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const dash = useQuery({ queryKey: ['dealer-finance-dash'], queryFn: () => dealerPaymentsApi.reconciliation() });
  const invoices = useQuery({ queryKey: ['dealer-invoices'], queryFn: () => dealerPaymentsApi.list({ entryType: 'invoice', limit: 200 }) });
  const dealers = useQuery({ queryKey: ['dealers', 'names'], queryFn: () => dealersApi.list({ limit: 500 }) });
  const dealerName = (id: number) => (dealers.data?.data.find((d) => d.id === id)?.dealerName) ?? `Dealer #${id}`;
  const refresh = () => { qc.invalidateQueries({ queryKey: ['dealer-finance-dash'] }); qc.invalidateQueries({ queryKey: ['dealer-invoices'] }); };

  const accept = useMutation({ mutationFn: (id: number) => dealerPaymentsApi.accept(id), onSuccess: () => { setToast('Invoice accepted'); refresh(); }, onError: (e) => setToast(extractApiError(e)) });
  const k = dash.data;

  return (
    <>
      <PageHeader title="Finance & Invoices" subtitle="Your invoices, payments and outstanding (Protection Device Installation)." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#1e40af" label="Total Invoiced" value={inr(k?.totalInvoiceValue)} icon={<ReceiptLong />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#16a34a" label="Collection Received" value={inr(k?.paidAmount)} icon={<Payments />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#d97706" label="Outstanding" value={inr(k?.outstandingAmount)} icon={<AccountBalanceWallet />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#dc2626" label="Overdue" value={inr(k?.overdueAmount)} icon={<Warning />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#0d9488" label="Credit Balance" value={inr(k?.creditBalance)} icon={<Savings />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#7c3aed" label="Total Received" value={inr(k?.totalCredit)} icon={<Payments />} /></Grid>
      </Grid>

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} md={4}><ChartCard title="Invoice Ageing" data={k?.ageing ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Monthly Payment Trend" data={k?.paymentTrend ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Monthly Invoiced" data={k?.invoiceTrend ?? []} /></Grid>
      </Grid>

      <Card>
        <CardHeader title="Dealer Ledger — Reconciliation & Ageing" titleTypographyProps={{ variant: 'subtitle1' }}
          subheader="Dealer-wise invoices with paid / outstanding, ageing and status. Use “Payment Received” to acknowledge a payment when the dealer hasn’t updated it." />
        <CardContent sx={{ p: 0, overflowX: 'auto' }}>
          {(invoices.data?.data.length ?? 0) === 0 ? <EmptyState title="No invoices" dense /> : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Dealer</TableCell><TableCell>Invoice</TableCell><TableCell align="right">Amount</TableCell><TableCell align="right">Paid</TableCell>
                <TableCell align="right">Outstanding</TableCell><TableCell>Due</TableCell><TableCell>Ageing</TableCell><TableCell>Status</TableCell><TableCell>Accepted</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {invoices.data!.data.map((inv) => {
                  const paid = inv.reconciliationStatus === 'reconciled' ? inv.amount : (inv.amountPaid ?? 0);
                  const outstanding = Math.max(0, inv.amount - paid);
                  const age = ageing(inv);
                  return (
                    <TableRow key={inv.id} hover>
                      <TableCell>{dealerName(inv.dealerId)}</TableCell>
                      <TableCell>{inv.paymentCode}</TableCell>
                      <TableCell align="right">{inr(inv.amount)}</TableCell>
                      <TableCell align="right">{inr(paid)}</TableCell>
                      <TableCell align="right">{inr(outstanding)}</TableCell>
                      <TableCell>{inv.dueDate ?? '—'}</TableCell>
                      <TableCell>{age.overdue ? <Chip size="small" color="error" label={age.text} /> : <span style={{ color: '#64748b' }}>{age.text}</span>}</TableCell>
                      <TableCell><Chip size="small" color={statusColor(inv.reconciliationStatus)} label={statusLabel(inv.reconciliationStatus)} /></TableCell>
                      <TableCell>{inv.acceptedAt ? <Chip size="small" color="success" label="Yes" /> : '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download"><IconButton size="small" onClick={() => dealerPaymentsApi.download(inv.id, 'pdf').catch((e) => setToast(extractApiError(e)))}><Download fontSize="small" /></IconButton></Tooltip>
                        {!inv.acceptedAt && <Tooltip title="Accept"><IconButton size="small" color="success" onClick={() => accept.mutate(inv.id)}><CheckCircle fontSize="small" /></IconButton></Tooltip>}
                        {inv.reconciliationStatus !== 'reconciled' && <Button size="small" variant="outlined" startIcon={<Payments />} onClick={() => setPayFor(inv)}>Payment Received</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {payFor && <MarkPaidDialog invoice={payFor} onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); setToast('Payment recorded'); refresh(); }} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function MarkPaidDialog({ invoice, onClose, onDone }: { invoice: DealerPayment; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState<MarkPaidInput>({ paymentDate: new Date().toISOString().slice(0, 10), paymentMode: 'upi', amountPaid: invoice.amount });
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof MarkPaidInput>(k: K, v: MarkPaidInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const mut = useMutation({ mutationFn: () => dealerPaymentsApi.markPaid(invoice.id, form), onSuccess: onDone, onError: (e) => setError(extractApiError(e)) });
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Payment Received — {invoice.paymentCode}</DialogTitle>
      <Alert severity="info" sx={{ mx: 3, mt: 2 }}>Record a payment received from the dealer (internal acknowledgement when the dealer hasn’t updated it).</Alert>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField label="Payment Date" type="date" InputLabelProps={{ shrink: true }} value={form.paymentDate} onChange={(e) => set('paymentDate', e.target.value)} fullWidth />
            <TextField select label="Payment Mode" value={form.paymentMode} onChange={(e) => set('paymentMode', e.target.value)} fullWidth>
              {PAY_MODES.map((m) => <MenuItem key={m} value={m}>{m.toUpperCase()}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Amount Paid" type="number" value={form.amountPaid} onChange={(e) => set('amountPaid', Number(e.target.value))} fullWidth />
            <TextField label="UTR Number" value={form.utrNumber ?? ''} onChange={(e) => set('utrNumber', e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Transaction Number" value={form.transactionNumber ?? ''} onChange={(e) => set('transactionNumber', e.target.value)} fullWidth />
            <FileUploadField label="Payment Proof" value={form.paymentProofUrl ?? null} onChange={(url) => set('paymentProofUrl', url ?? '')} />
          </Stack>
          <TextField label="Remarks" value={form.remarks ?? ''} onChange={(e) => set('remarks', e.target.value)} multiline minRows={2} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending} onClick={() => mut.mutate()}>Mark Paid</Button>
      </DialogActions>
    </Dialog>
  );
}
